import { getDatabase, ref, get } from 'firebase/database';
import { app } from '../firebase';
import {
  getCachedData,
  setCachedData,
  buildCacheKey,
  getCachedDataWithVersion,
  getCachedEntry,
  isCacheValid,
  getIdbCachedData,
  setIdbCachedData,
  removeIdbCachedData,
  removeCachedData,
  removeCachePrefix,
  clearIdbCache,
} from '../utils/cache';

const CACHE_TTL = {
  STATIC: 7 * 24 * 60 * 60 * 1000,
  CONFIG: 24 * 60 * 60 * 1000,
  USER_SPECIFIC: 24 * 60 * 60 * 1000,
  SHORT: 5 * 60 * 1000,
};

const QUIZ_BANK_IDB_KEY = 'quiz_bank_v1';
const QUIZ_BANK_LEGACY_KEYS = [
  'areas__legacy',
  'temas__legacy',
  'preguntas_por_tema__legacy',
  'areas',
  'temas',
  'preguntas_legacy',
  'preguntas',
];
const QUIZ_BANK_REVALIDATE_MS = 5 * 60 * 1000;
const QUIZ_BANK_VERSION_PATHS = [
  'cache_control/areas_version',
  'cache_control/temas_version',
  'cache_control/preguntas_updated',
];

let _quizBankMemory = null;
let _quizBankPromise = null;
let _quizBankRevalidationPromise = null;
let _quizBankLastCheck = 0;
let _quizBankRevalidating = false;
const _revalidatingKeys = new Set();
const _especInFlight = new Map();

function rtdb() {
  return getDatabase(app);
}

function toNumericId(value) {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? value : parsed;
}

function normalizeAreas(value) {
  if (!value) return [];
  const entries = Array.isArray(value)
    ? value.map((area, index) => [area?.id ?? index, area])
    : Object.entries(value);
  return entries
    .filter(([, area]) => area && typeof area === 'object')
    .map(([id, area]) => ({ ...area, id: toNumericId(id) }));
}

function normalizeTemas(value) {
  if (!value) return [];
  const entries = Array.isArray(value)
    ? value.map((tema, index) => [tema?.id ?? index, tema])
    : Object.entries(value);
  return entries
    .filter(([, tema]) => tema && typeof tema === 'object')
    .map(([id, tema]) => ({ ...tema, id: toNumericId(id) }));
}

function buildQuestionId(temaId, rawId) {
  const raw = rawId === undefined || rawId === null ? '' : String(rawId);
  if (temaId === undefined || temaId === null || temaId === '') return raw;
  if (!raw) return String(temaId);
  if (raw.startsWith('q_') || raw.startsWith(`${temaId}_`)) return raw;
  return `${temaId}_${raw}`;
}

function normalizePreguntas(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .filter((question) => question && typeof question === 'object')
      .map((question, index) => {
        const rawTemaId = question.tema_id ?? question.temaId;
        const temaId = rawTemaId === undefined || rawTemaId === null ? undefined : toNumericId(rawTemaId);
        return {
          ...question,
          id: buildQuestionId(temaId, question.id ?? index),
          tema_id: temaId,
        };
      });
  }

  if (typeof value !== 'object') return [];

  const preguntas = [];
  Object.entries(value).forEach(([temaIdRaw, preguntasMap]) => {
    if (!preguntasMap) return;
    const temaId = toNumericId(temaIdRaw);

    if (Array.isArray(preguntasMap)) {
      preguntasMap.forEach((question, index) => {
        if (!question || typeof question !== 'object') return;
        const rawTemaId = question.tema_id ?? question.temaId;
        const resolvedTemaId = rawTemaId === undefined || rawTemaId === null ? temaId : toNumericId(rawTemaId);
        preguntas.push({
          ...question,
          id: buildQuestionId(resolvedTemaId, question.id ?? index),
          tema_id: resolvedTemaId,
        });
      });
      return;
    }

    if (typeof preguntasMap === 'object') {
      Object.entries(preguntasMap).forEach(([questionId, question]) => {
        if (!question || typeof question !== 'object') return;
        const rawTemaId = question.tema_id ?? question.temaId;
        const resolvedTemaId = rawTemaId === undefined || rawTemaId === null ? temaId : toNumericId(rawTemaId);
        preguntas.push({
          ...question,
          id: buildQuestionId(resolvedTemaId, questionId),
          tema_id: resolvedTemaId,
        });
      });
    }
  });
  return preguntas;
}

function inferTemaId(question, temas) {
  const candidates = [question.tema_id, question.temaId, question.tema];
  for (const raw of candidates) {
    if (raw === undefined || raw === null || raw === '') continue;
    const parsed = parseInt(raw, 10);
    if (!Number.isNaN(parsed) && (typeof raw === 'number' || String(parsed) === String(raw).trim())) {
      return parsed;
    }
    const name = String(raw).toLowerCase().trim();
    const match = temas.find((tema) => {
      const temaName = (tema.nombre || '').toLowerCase().trim();
      return temaName && (name === temaName || name.includes(temaName) || temaName.includes(name));
    });
    if (match) return toNumericId(match.id);
  }
  return undefined;
}

function normalizeLegacyPreguntas(value, temas) {
  if (!value || typeof value !== 'object') return [];
  return Object.entries(value)
    .map(([id, question]) => {
      if (!question || typeof question !== 'object') return null;
      return {
        ...question,
        id,
        tema_id: inferTemaId(question, temas),
      };
    })
    .filter(Boolean);
}

async function fetchVersionNode(path) {
  try {
    const snapshot = await get(ref(rtdb(), path));
    return snapshot.exists() ? snapshot.val() : null;
  } catch {
    return null;
  }
}

async function fetchQuizBankVersion() {
  const values = await Promise.all(QUIZ_BANK_VERSION_PATHS.map(fetchVersionNode));
  if (values.every((value) => value == null)) return null;
  return values.map((value) => (value == null ? '' : String(value))).join('|');
}

async function fetchQuizBankRemote() {
  const database = rtdb();
  const [version, areasSnapshot, temasSnapshot, preguntasSnapshot] = await Promise.all([
    fetchQuizBankVersion(),
    get(ref(database, 'areas')),
    get(ref(database, 'temas')),
    get(ref(database, 'preguntas_por_tema')),
  ]);

  const areas = normalizeAreas(areasSnapshot.exists() ? areasSnapshot.val() : null);
  const temas = normalizeTemas(temasSnapshot.exists() ? temasSnapshot.val() : null);
  let preguntas = normalizePreguntas(preguntasSnapshot.exists() ? preguntasSnapshot.val() : null);

  if (preguntas.length === 0) {
    try {
      const legacySnapshot = await get(ref(database, 'preguntas'));
      if (legacySnapshot.exists()) {
        preguntas = normalizeLegacyPreguntas(legacySnapshot.val(), temas);
      }
    } catch {}
  }

  return { areas, temas, preguntas, version, timestamp: Date.now() };
}

function readLegacyQuizBank() {
  const candidates = [
    ['areas__legacy', 'temas__legacy', 'preguntas_por_tema__legacy'],
    ['areas', 'temas', 'preguntas_legacy'],
    ['areas', 'temas', 'preguntas'],
  ];

  for (const keys of candidates) {
    const entries = keys.map((key) => getCachedEntry(key));
    if (entries.some((entry) => !entry)) continue;

    const [areasEntry, temasEntry, preguntasEntry] = entries;
    const areas = normalizeAreas(areasEntry.data);
    const temas = normalizeTemas(temasEntry.data);
    const preguntas = normalizePreguntas(preguntasEntry.data);
    if (areas.length === 0 || temas.length === 0 || preguntas.length === 0) continue;

    const timestamps = entries.map((entry) => entry.timestamp).filter(Boolean);
    return {
      areas,
      temas,
      preguntas,
      version: preguntasEntry.version ?? temasEntry.version ?? areasEntry.version ?? null,
      timestamp: timestamps.length > 0 ? Math.min(...timestamps) : Date.now(),
    };
  }
  return null;
}

async function persistQuizBank(bank) {
  _quizBankMemory = bank;
  const saved = await setIdbCachedData(
    QUIZ_BANK_IDB_KEY,
    { areas: bank.areas, temas: bank.temas, preguntas: bank.preguntas },
    bank.version
  );
  if (saved) {
    QUIZ_BANK_LEGACY_KEYS.forEach((key) => removeCachedData(key));
  }
  return saved;
}

function scheduleQuizBankRevalidation() {
  if (_quizBankRevalidating) return;
  if (Date.now() - _quizBankLastCheck < QUIZ_BANK_REVALIDATE_MS) return;
  _quizBankRevalidating = true;
  revalidateQuizBank()
    .catch(() => {})
    .finally(() => {
      _quizBankRevalidating = false;
    });
}

export async function loadQuizBank({ forceRefresh = false } = {}) {
  if (forceRefresh) {
    _quizBankMemory = null;
    _quizBankLastCheck = 0;
  }
  if (_quizBankMemory) {
    scheduleQuizBankRevalidation();
    return _quizBankMemory;
  }
  if (_quizBankPromise) return _quizBankPromise;

  _quizBankPromise = (async () => {
    const idbEntry = await getIdbCachedData(QUIZ_BANK_IDB_KEY, 0);
    if (idbEntry?.data) {
      const bank = {
        areas: idbEntry.data.areas || [],
        temas: idbEntry.data.temas || [],
        preguntas: idbEntry.data.preguntas || [],
        version: idbEntry.version ?? null,
        timestamp: idbEntry.timestamp || Date.now(),
      };
      _quizBankMemory = bank;
      scheduleQuizBankRevalidation();
      return bank;
    }

    const legacy = readLegacyQuizBank();
    if (legacy) {
      _quizBankMemory = legacy;
      persistQuizBank(legacy).catch(() => {});
      scheduleQuizBankRevalidation();
      return legacy;
    }

    try {
      const bank = await fetchQuizBankRemote();
      _quizBankLastCheck = Date.now();
      await persistQuizBank(bank);
      return bank;
    } catch (error) {
      console.warn('[rtdbCache] No se pudo cargar el banco de preguntas:', error);
      return { areas: [], temas: [], preguntas: [], version: null, timestamp: 0 };
    }
  })();

  try {
    return await _quizBankPromise;
  } finally {
    _quizBankPromise = null;
  }
}

export async function revalidateQuizBank() {
  if (!_quizBankMemory) return null;
  if (_quizBankRevalidationPromise) return _quizBankRevalidationPromise;

  _quizBankRevalidationPromise = (async () => {
    try {
      const remoteVersion = await fetchQuizBankVersion();
      const current = _quizBankMemory;
      const versionChanged = remoteVersion != null && current?.version !== remoteVersion;
      const missingData = !current || current.preguntas.length === 0;
      if (!versionChanged && !missingData) return null;

      const fresh = await fetchQuizBankRemote();
      await persistQuizBank(fresh);
      return fresh;
    } catch (error) {
      console.warn('[rtdbCache] No se pudo revalidar el banco de preguntas:', error);
      return null;
    } finally {
      _quizBankLastCheck = Date.now();
      _quizBankRevalidationPromise = null;
    }
  })();

  return _quizBankRevalidationPromise;
}

export async function prefetchQuizBank() {
  return loadQuizBank();
}

export function invalidateQuizBankCache() {
  _quizBankMemory = null;
  _quizBankLastCheck = 0;
  removeIdbCachedData(QUIZ_BANK_IDB_KEY).catch(() => {});
}

function scheduleKeyRevalidation(key, path, versionPath, ttl) {
  if (_revalidatingKeys.has(key)) return;
  _revalidatingKeys.add(key);
  (async () => {
    try {
      const remoteVersion = versionPath ? await fetchVersionNode(versionPath) : null;
      const cached = getCachedDataWithVersion(key, ttl);
      const hasData = cached && cached.data !== undefined && cached.data !== null;
      const versionChanged = versionPath && remoteVersion != null && cached && cached.version !== remoteVersion;
      if (!hasData || versionChanged) {
        const data = await fetchFromRTDB(path);
        if (data !== null) setCachedData(key, data, remoteVersion);
      }
    } catch {}
    finally {
      _revalidatingKeys.delete(key);
    }
  })();
}

async function fetchFromRTDB(path) {
  const snapshot = await get(ref(rtdb(), path));
  if (!snapshot.exists()) return null;
  return snapshot.val();
}

export async function getCached(path, options = {}) {
  const {
    ttl = CACHE_TTL.STATIC,
    versionPath = null,
    transform = (value) => value,
    fallback = null,
    cacheKey = null,
  } = options;

  const key = cacheKey || buildCacheKey(path);

  if (versionPath) {
    const cached = getCachedDataWithVersion(key, ttl);
    if (cached && isCacheValid(cached, ttl)) {
      // Devolver el caché de inmediato y revalidar la versión en segundo plano.
      scheduleKeyRevalidation(key, path, versionPath, ttl);
      return transform(cached.data);
    }
  } else {
    const cached = getCachedData(key, ttl);
    if (cached !== null) {
      return transform(cached);
    }
  }

  try {
    const [data, version] = await Promise.all([
      fetchFromRTDB(path),
      versionPath ? fetchVersionNode(versionPath) : Promise.resolve(null),
    ]);
    if (data === null) return fallback;

    setCachedData(key, data, version);
    return transform(data);
  } catch (error) {
    console.warn(`[rtdbCache] Error fetching ${path}:`, error);
    return fallback;
  }
}

export async function getCachedAreas() {
  const bank = await loadQuizBank();
  return bank.areas;
}

export async function getCachedTemas() {
  const bank = await loadQuizBank();
  return bank.temas;
}

export async function getCachedPreguntasPorTema() {
  const bank = await loadQuizBank();
  return bank.preguntas;
}

export async function getCachedModelos() {
  return getCached('config/apis/modelos', {
    ttl: CACHE_TTL.CONFIG,
    transform: (value) => {
      if (!value || typeof value !== 'object') return {};
      const models = {};
      Object.entries(value).forEach(([key, modelValue]) => {
        if (typeof modelValue === 'object' && modelValue !== null) {
          models[key] = {
            displayName: modelValue.displayName || key,
            modelName: modelValue.modelName || key,
            apiKey: modelValue.apiKey || modelValue.gemini || '',
            provider: modelValue.provider || 'openrouter',
          };
        } else {
          models[key] = {
            displayName: `${key} (${modelValue})`,
            modelName: modelValue?.toString() || key,
            apiKey: '',
            provider: 'openrouter',
          };
        }
      });
      return models;
    },
    fallback: {},
  });
}

export async function getCachedEspecialidadesTemas(hierarchyCode, corpsCode, specialtyCode) {
  if (!hierarchyCode || !corpsCode || !specialtyCode) return [];
  const path = `especialidades_temas/${hierarchyCode}/${corpsCode}/${specialtyCode}/temas`;
  const cacheKey = buildCacheKey(`espec_${hierarchyCode}_${corpsCode}_${specialtyCode}`);

  const localCached = getCachedData(cacheKey, CACHE_TTL.USER_SPECIFIC);
  if (Array.isArray(localCached) && localCached.length > 0) {
    return localCached;
  }

  const idbCached = await getIdbCachedData(cacheKey, CACHE_TTL.USER_SPECIFIC);
  if (Array.isArray(idbCached?.data) && idbCached.data.length > 0) {
    return idbCached.data;
  }

  if (_especInFlight.has(cacheKey)) {
    return _especInFlight.get(cacheKey);
  }

  const request = (async () => {
    try {
      const data = await fetchFromRTDB(path);
      const result = [];
      if (data && typeof data === 'object') {
        Object.entries(data).forEach(([temaId, temaObj]) => {
          if (!temaObj || !temaObj.cuestionarios) return;
          result.push({
            temaId: parseInt(temaId, 10),
            nombre: temaObj.nombre || `Especialidad Tema ${temaId}`,
            cuestionarios: temaObj.cuestionarios,
          });
        });
      }
      await setIdbCachedData(cacheKey, result, null);
      return result;
    } catch (error) {
      console.warn('[rtdbCache] Error fetching especialidades_temas:', error);
      return [];
    }
  })();

  _especInFlight.set(cacheKey, request);
  try {
    return await request;
  } finally {
    _especInFlight.delete(cacheKey);
  }
}

export async function getCachedLicenseTokens(encodedEmail) {
  const path = `licencesPremium/${encodedEmail}`;
  const cacheKey = buildCacheKey(`license_${encodedEmail}`);

  const cached = getCachedData(cacheKey, CACHE_TTL.SHORT);
  if (cached !== null) {
    return cached;
  }

  try {
    const data = await fetchFromRTDB(path);
    if (!data) return null;
    const tokens = {
      tokensAvailable: data.tokensAvailable || 2000000,
      tokensUsed: data.tokensUsed || 0,
      expiresAt: data.expiresAt || data.tokenPeriodEnd || null,
    };
    setCachedData(cacheKey, tokens, null);
    return tokens;
  } catch (error) {
    console.warn(`[rtdbCache] Error fetching license for ${encodedEmail}:`, error);
    return null;
  }
}

export function invalidateCache(pattern) {
  try {
    const prefix = buildCacheKey(pattern);
    Object.keys(localStorage)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => localStorage.removeItem(key));
  } catch {}

  if (/(quiz_bank|areas|temas|preguntas)/.test(String(pattern))) {
    invalidateQuizBankCache();
  }
}

export function clearAllCache() {
  try {
    removeCachePrefix('vortex_cache_');
  } catch {}
  invalidateQuizBankCache();
  clearIdbCache().catch(() => {});
}
